import { Component, OnInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HTTP_STATUS_CODES, MAX_PHONE_DIGITS, OTP_LENGTH } from 'src/constants';
import { takeUntil } from 'rxjs/operators';
import { LocalStorageService } from '../../../services/local-storage.service';
import { BaseComponent } from '../../../shared/base.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class AdminLoginComponent extends BaseComponent implements OnInit {
  reactiveForm!: FormGroup;
  protected maxPhoneDigits = MAX_PHONE_DIGITS;
  protected otpLength = OTP_LENGTH;
  protected otp_requested: boolean = false;
  protected is_loading: boolean = false;
  protected is_authorized: boolean = true;
  protected otp_error: boolean = false;
  protected max_attempts_reached: boolean = false;
  protected phone_number_error: boolean = false;
  protected unexpected_error: boolean = false;
  protected otpParts = Array.from({ length: OTP_LENGTH }, (_, i) => `otp${i + 1}`);
  
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  
  private state_token = '';
  mode = 'login';
  headerLabel = 'Login';
  formLabel = 'Enter the Mobile Number you have registered with us for this platform';
  
  constructor(
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private localStorage: LocalStorageService,
    private router: Router
  ) {
    super();
  }
  
  ngOnInit(): void {
    this.initForm();
    
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.mode = params['mode'] || 'login';
        this.updateLabels();
      });
  
    this.get_phone_number?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(newValue => {
        if (newValue?.toString().length !== 0) {
          this.is_authorized = true;
          this.max_attempts_reached = false;
        }
      });
  }
  
  protected get get_phone_number() { return this.reactiveForm.get('phone_number'); }
  
  private updateLabels(): void {
    if (this.mode === 'login') {
      this.headerLabel = 'Login';
      this.formLabel = 'Enter the Mobile Number you have registered with us for this platform';
    } else if (this.mode === 'register') {
      this.headerLabel = 'Registration';
      this.formLabel = 'Enter the Mobile Number you want to Register with us for this platform';
    }
  }
  
  private initForm(): void {
    const controls: any = {
      phone_number: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{10}$')
      ])
    };
    
    this.otpParts.forEach(name => {
      controls[name] = new FormControl('', Validators.required);
    });
    
    this.reactiveForm = new FormGroup(controls);
  }
  
  protected onOtpInputKeyUp(event: KeyboardEvent, nextIndex: number, prevIndex: number): void {
    const target = event.target as HTMLInputElement;
    
    if (event.key !== 'Backspace' && event.key !== 'Tab') {
      this.otp_error = false;
      if (target.value && nextIndex !== -1) {
        this.otpInputs.toArray()[nextIndex].nativeElement.focus();
      }
    } else if (event.key === 'Backspace') {
      target.value = '';
      if (prevIndex !== -1) {
        this.otpInputs.toArray()[prevIndex].nativeElement.focus();
      }
    }
  }
  
  private setRequestOtpState(): void {
    this.is_loading = false;
    this.otp_requested = true;
    this.resetOtp();
    setTimeout(() => {
      this.otpInputs?.first?.nativeElement.focus();
    }, 100);
  }
  
  private setRequestPhoneState(): void {
    this.max_attempts_reached = false;
    this.is_loading = false;
    this.otp_requested = false;
    this.otp_error = false;
    this.unexpected_error = false;
    this.resetOtp();
    this.get_phone_number?.reset();
  }
  
  private resetOtp(): void {
    this.otpParts.forEach(name => this.reactiveForm.get(name)?.reset());
  }
  
  private processError(error: any): void {
    this.setRequestPhoneState();
    if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
      this.is_authorized = false;
    } else {
      this.unexpected_error = true;
    }
  }
  
  protected async onOtpSubmit(): Promise<void> {
    const otp = this.otpParts.map(name => this.reactiveForm.get(name)?.value).join('');
  
    if (otp.length !== this.otpLength || !this.state_token) {
      this.otp_error = true;
      return;
    }
  
    this.is_loading = true;
  
    this.auth.verifyOtp(this.state_token, otp)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (res) => {
          if (res.status === 'SUCCESS') {
            if (res.role !== 'SuperAdmin') {
              this.otp_error = true;
              this.setRequestOtpState();
              return;
            }

            await this.auth.setToken(res.session_token);
            if (res.name) this.auth.setUser(res.name);
            this.auth.setRole(res.role);
            this.router.navigateByUrl('/admin-dash/dashboard');
            return;
          }
  
          if (res.status === 'OTP_RESTRICTED') {
            this.setRequestPhoneState();
            this.max_attempts_reached = true;
            return;
          }
  
          this.setRequestOtpState();
          this.otp_error = true;
        },
        error: (err) => this.processError(err),
        complete: () => this.is_loading = false
      });
  }
  
  protected onPhoneNumberSubmit(): void {
    const phoneNumber = this.get_phone_number?.value;
    
    if (!phoneNumber || phoneNumber.toString().length !== 10) {
      this.phone_number_error = true;
      return;
    }
  
    this.is_loading = true;
    this.phone_number_error = false;

    this.auth.requestOtpForUser(phoneNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.status === 'RESTRICTED') {
            this.setRequestPhoneState();
            this.max_attempts_reached = true;
            return;
          }
          
          this.setRequestOtpState();
          this.state_token = res.state_token;
          this.localStorage.setPhoneNumber(phoneNumber);
        },
        error: (err) => this.processError(err),
        complete: () => this.is_loading = false
      });
  }
  
  protected isOtpFormValid(): boolean {
    return this.otpParts.every(part => {
      const control = this.reactiveForm.get(part);
      return control?.valid && control.value !== null && control.value !== '';
    });
  }

  protected goBackToPhoneInput(): void {
    this.otp_requested = false;
    this.is_loading = false;
    this.otp_error = false;
    this.reactiveForm.reset();
  }
}